import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, EmptyState, Menu, Skeleton } from "../../../design-v2";
import { CURRENT_ANALYSIS, HISTORICAL_TIMEFRAMES } from "../api/insightsApi";
import { TIMEFRAME_LABELS } from "../domain/insightsPresentation";
import { availableTimeframes } from "../domain/timeframeAvailability";
import { useAdherence } from "../screens/useInsightsData";

/* Shared Insights chrome: timeframe selector, section shells and states. */

function ChevronDownIcon() {
  return (
    <svg className="lz-insights-icon lz-insights-icon--sm" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function ChevronRightIcon() {
  return (
    <svg className="lz-insights-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="lz-insights-icon lz-insights-icon--sm" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

/*
 * Which historical timeframes add history (domain/timeframeAvailability.js),
 * read from the ALL_TIME adherence response. Requested as soon as the
 * selector mounts, in parallel with the screen's own reads, so the menu is
 * already resolved when it opens. `null` while that read is pending; every
 * timeframe if it fails, rather than hiding one wrongly.
 */
function useAvailableTimeframes() {
  const history = useAdherence("ALL_TIME");
  if (history.status === "loading") return null;
  return (history.status === "success" && availableTimeframes(history.data)) || HISTORICAL_TIMEFRAMES;
}

/*
 * [ CURRENT ANALYSIS | 3 MONTHS ▾ ] (Hub, Adherence and Muscle Volume): a
 * compact segmented control that sticks under the screen header. Current
 * Analysis is a toggle; the historical horizons live in a menu whose trigger
 * shows the chosen horizon. With a single horizon worth offering the
 * trigger switches to it directly instead of opening a one-item menu. The
 * selected timeframe always stays listed. `children` stack as a second row in
 * the same sticky bar (Muscle Volume's set-count control).
 */
export function TimeframeSelector({ value, onChange, children = null }) {
  const [open, setOpen] = useState(false);
  const isCurrent = value === CURRENT_ANALYSIS;
  const available = useAvailableTimeframes();
  const timeframes = available
    ? HISTORICAL_TIMEFRAMES.filter((timeframe) => available.includes(timeframe) || timeframe === value)
    : [];
  const single = timeframes.length === 1 ? timeframes[0] : null;

  return (
    <div className="lz-insights-subnav">
      <div className="lz-insights-timeframe" role="group" aria-label="Analysis period">
        <button
          type="button"
          className="lz-insights-timeframe__option"
          aria-pressed={isCurrent}
          onClick={() => onChange(CURRENT_ANALYSIS)}
        >
          {isCurrent ? <span className="lz-insights-timeframe__dot" aria-hidden="true" /> : null}
          Current Analysis
        </button>
        {single ? (
          <button
            type="button"
            className="lz-insights-timeframe__option"
            aria-pressed={!isCurrent}
            aria-label={`Timeframe: ${TIMEFRAME_LABELS[single]}`}
            onClick={() => onChange(single)}
          >
            {TIMEFRAME_LABELS[single]}
          </button>
        ) : (
          <Menu
            open={open}
            onOpenChange={setOpen}
            className="lz-insights-timeframe__menu"
            menuClassName="lz-insights-timeframe__surface"
            aria-label="Timeframes"
            trigger={(
              <button
                type="button"
                className="lz-insights-timeframe__option"
                aria-pressed={!isCurrent}
                aria-label={isCurrent ? "Timeframe" : `Timeframe: ${TIMEFRAME_LABELS[value]}`}
                disabled={!available}
                aria-busy={!available || undefined}
              >
                {isCurrent ? "Timeframe" : TIMEFRAME_LABELS[value]}
                <ChevronDownIcon />
              </button>
            )}
          >
            {timeframes.map((timeframe) => (
              <button
                key={timeframe}
                type="button"
                role="menuitemradio"
                aria-checked={value === timeframe}
                className="lz-insights-timeframe__item"
                onClick={() => {
                  onChange(timeframe);
                  setOpen(false);
                }}
              >
                {TIMEFRAME_LABELS[timeframe]}
                {value === timeframe ? <CheckIcon /> : null}
              </button>
            ))}
          </Menu>
        )}
      </div>
      {children}
    </div>
  );
}

// A Hub card / detail section: title, optional subtitle, optional link.
export function InsightsSection({ title, subtitle, to, linkLabel, headingLevel = "h2", children, testId, aside }) {
  const Heading = headingLevel;
  return (
    <section className="lz-insights-card" data-testid={testId} aria-label={title}>
      <div className="lz-insights-card__head">
        <div className="lz-insights-card__titles">
          <Heading className="lz-insights-card__title">{title}</Heading>
          {subtitle ? <p className="lz-insights-card__subtitle">{subtitle}</p> : null}
        </div>
        {aside}
        {to ? (
          <Link className="lz-insights-card__link" to={to} aria-label={linkLabel}>
            <ChevronRightIcon />
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function SectionLoading({ label }) {
  return (
    <div className="lz-insights-loading" aria-busy="true">
      <Skeleton shape="block" height={20} width="45%" aria-label={label} />
      <Skeleton shape="block" height={132} />
      <Skeleton shape="block" height={168} />
    </div>
  );
}

// Endpoint failure: a plain sentence and a retry, never the raw error.
export function SectionError({ what, onRetry }) {
  return (
    <div className="lz-insights-error" role="alert">
      <p className="lz-insights-error__text">{`We couldn't load ${what} right now.`}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

// Current Analysis unavailable (§15.2: no cycle active today). Historical
// timeframes stay available, so the action switches to one.
export function NoActiveCycleState({ onBrowseHistory, titleAs = "h2" }) {
  return (
    <EmptyState
      className="lz-insights-empty"
      titleAs={titleAs}
      title="No active cycle"
      body="Current Analysis compares the cycle you are training in today with your previous one. It starts when a cycle is active. Your history stays available by timeframe."
      action={onBrowseHistory ? (
        <Button variant="secondary" onClick={onBrowseHistory}>
          View last 3 months
        </Button>
      ) : null}
      data-testid="no-active-cycle"
    />
  );
}

export function NoHistoryState({ timeframe, titleAs = "h2" }) {
  return (
    <EmptyState
      className="lz-insights-empty"
      titleAs={titleAs}
      title="No training data yet"
      body={timeframe === "ALL_TIME"
        ? "Nothing has been planned or logged yet."
        : `Nothing was planned or logged in the last ${TIMEFRAME_LABELS[timeframe].toLowerCase()}.`}
      data-testid="no-history"
    />
  );
}
